/**
 * Ticket Lifecycle v2 — FOH Timer Handoff Batch 1
 *
 * Pure helpers that translate a custody transition into the OCIS
 * timeline event payload. Keeps the mapping out of the mutation hook
 * so it is unit-testable without React Query / Supabase.
 *
 * `logTicketEventToOcis` (src/utils/ocisIngest.ts) is the I/O sink;
 * this module only builds the (eventType, content, payload) tuple.
 */
import type { OcisEventType } from '@/utils/ocisIngest';
import type { CustodyEventKind } from '@/lib/tickets/lifecycle/types';
import {
  getCustodyHandoffReasonLabel,
  readCustodyHandoffMetadata,
  type CustodyHandoffMetadata,
} from '@/lib/tickets/lifecycle/custodyHandoffReasons';

export interface CustodyOcisEventPlan {
  eventType: OcisEventType;
  content: string;
  payload: Record<string, unknown>;
}

/**
 * Maps a `CustodyEventKind` to its OCIS event-type string. Returns
 * `null` for unrecognised kinds so a future event kind without an
 * OCIS analog is silently skipped rather than emitting a wrong type.
 */
export function ocisEventTypeForCustodyKind(
  kind: CustodyEventKind | string,
): OcisEventType | null {
  switch (kind) {
    case 'kick_to_foh':
      return 'custody_kick_to_foh';
    case 'foh_accept':
      return 'custody_foh_accept';
    case 'kick_to_boh':
      return 'custody_kick_to_boh';
    case 'boh_accept':
      return 'custody_boh_accept';
    case 'reject_back':
      return 'custody_reject_back';
    case 'reassign_within_foh':
      return 'custody_reassign_within_foh';
    case 'reassign_within_boh':
      return 'custody_reassign_within_boh';
    default:
      return null;
  }
}

function defaultLabelForKind(kind: CustodyEventKind | string): string {
  switch (kind) {
    case 'kick_to_foh':
      return 'Kicked to advisor';
    case 'foh_accept':
      return 'Advisor accepted ticket';
    case 'kick_to_boh':
      return 'Kicked back to tech';
    case 'boh_accept':
      return 'Tech accepted return';
    case 'reject_back':
      return 'Rejected back';
    case 'reassign_within_foh':
      return 'Advisor reassigned';
    case 'reassign_within_boh':
      return 'Technician reassigned';
    default:
      return 'Custody updated';
  }
}

export interface BuildCustodyOcisEventArgs {
  eventKind: CustodyEventKind | string;
  reason: string | null;
  /** May be a standardized CustodyHandoffMetadata or a legacy jsonb blob. */
  metadata?: unknown;
  custodyEventId?: string | null;
}

/**
 * Builds the OCIS payload for a custody transition. Returns `null`
 * when the kind has no OCIS analog. Pure.
 */
export function buildCustodyOcisEvent(
  args: BuildCustodyOcisEventArgs,
): CustodyOcisEventPlan | null {
  const eventType = ocisEventTypeForCustodyKind(args.eventKind);
  if (!eventType) return null;

  const md: CustodyHandoffMetadata | null = readCustodyHandoffMetadata(
    args.metadata,
  );
  const reasonLabel =
    md?.trigger_reason_label ?? getCustodyHandoffReasonLabel(args.reason);
  const headline = defaultLabelForKind(args.eventKind);
  const hasReason = !!args.reason || !!md?.trigger_reason;
  const content = hasReason
    ? `${headline} — ${reasonLabel}`
    : headline;

  const payload: Record<string, unknown> = {
    custody_event_kind: args.eventKind,
  };
  if (args.custodyEventId) payload.custody_event_id = args.custodyEventId;
  if (args.reason != null) payload.reason = args.reason;
  if (md) {
    payload.trigger = md.trigger;
    payload.trigger_reason = md.trigger_reason;
    if (md.trigger_reason_label != null)
      payload.trigger_reason_label = md.trigger_reason_label;
    if (md.timer_status_at !== undefined)
      payload.timer_status_at = md.timer_status_at;
    if (md.pause_reason !== undefined)
      payload.pause_reason = md.pause_reason;
    if (md.source !== undefined) payload.source = md.source;
    if (md.source_component !== undefined)
      payload.source_component = md.source_component;
    if (md.notes != null) payload.notes = md.notes;
  }

  return { eventType, content, payload };
}
